import React from "react";
import { View, Text, StyleSheet, ScrollView, TouchableOpacity } from "react-native";
import { Image } from "expo-image";
import { Ionicons, FontAwesome6 } from "@expo/vector-icons";
import { useTheme } from "../../../context/ThemeContext";
import { useContent } from "../../../context/ContentContext";
import { useProfilePage } from "../ProfilePage";
import { withComponentEditor } from "../../BaseComponent";
const componentType = "scrollableCanvas";
// Mock data for posts tab
const mockPosts = [
	{
		id: "post1",
		type: "Regular",
		author: { name: "Jane Doe", avatar: "https://picsum.photos/seed/avatar1/40/40" },
		createdAt: new Date(Date.now() - 1000 * 60 * 5).toISOString(), // 5 minutes ago
		text: "Just enjoyed a beautiful sunset! 🌅 #nature #photography",
		images: ["https://picsum.photos/seed/post1img1/600/400"],
		likeCount: 15,
		commentCount: 3,
	},
	{
		id: "post2",
		type: "Regular",
		author: { name: "John Smith", avatar: "https://picsum.photos/seed/avatar2/40/40" },
		createdAt: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(), // 2 hours ago
		text: "Exploring the city today. So much to see!",
		images: [],
		likeCount: 5,
		commentCount: 1,
	},
	{
		id: "post3",
		type: "Featured",
		title: "Upcoming Workshop: React Native Basics",
		text: "Join us next week for an interactive workshop covering the fundamentals of React Native development.",
		linkType: "event",
		event: {
			id: "event1",
			title: "React Native Basics Workshop",
			date: new Date(Date.now() + 1000 * 60 * 60 * 24 * 7).toISOString(), // 1 week from now
			location: "Online",
		},
		likeCount: 42,
		commentCount: 8,
	},
	{
		id: "post4",
		type: "Regular",
		author: { name: "Alice Green", avatar: "https://picsum.photos/seed/avatar4/40/40" },
		createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(), // 1 day ago
		text: "My new photo setup. What do you think?",
		images: ["https://picsum.photos/seed/post4img1/600/400", "https://picsum.photos/seed/post4img2/600/400"],
		likeCount: 33,
		commentCount: 7,
	},
];

// MockPost component for rendering various post types in the posts tab
const MockPost = ({ content }) => {
	const theme = useTheme();
	const postStyles = usePostStyles();
	const [isLiked, setIsLiked] = React.useState(false);
	const [localLikeCount, setLocalLikeCount] = React.useState(content.likeCount || 0);

	const handleLike = () => {
		const newIsLiked = !isLiked;
		setIsLiked(newIsLiked);
		setLocalLikeCount((prevCount) => (newIsLiked ? prevCount + 1 : Math.max(prevCount - 1, 0)));
	};

	const { author, createdAt, text, images = [], type, title, event } = content;
	const formattedDate = createdAt ? new Date(createdAt).toLocaleTimeString() : "";

	return (
		<View style={postStyles.container}>
			{type !== "Featured" && author && (
				<View style={postStyles.header}>
					<View style={postStyles.headerLeft}>
						<Image source={{ uri: author.avatar }} style={postStyles.avatar} cachePolicy="memory" />
						<View style={postStyles.authorContainer}>
							<Text style={postStyles.authorName}>{author.name || "Unknown User"}</Text>
							<Text style={postStyles.timestamp}>{formattedDate}</Text>
						</View>
					</View>
				</View>
			)}

			{type === "Featured" && (
				<View style={postStyles.featuredHeader}>
					<Ionicons name={event ? "calendar-outline" : "star-outline"} size={16} color={theme.colors.primary} style={{ marginRight: 5 }} />
					<Text style={postStyles.featuredLabel}>{title}</Text>
				</View>
			)}

			<View style={postStyles.contentContainer}>
				<Text style={postStyles.postText}>{text}</Text>
				{images && images.length > 0 && (
					<View style={postStyles.imageContainer}>
						<Image source={{ uri: images[0] }} style={postStyles.postImagePreview} contentFit="cover" />
						{images.length > 1 && (
							<View style={postStyles.moreImagesOverlay}>
								<Text style={postStyles.moreImagesText}>+{images.length - 1}</Text>
							</View>
						)}
					</View>
				)}
				{event && (
					<View style={postStyles.eventPreview}>
						<Text style={postStyles.eventTitle}>{event.title}</Text>
						<Text style={postStyles.eventDetails}>
							{new Date(event.date).toLocaleDateString()} at {event.location}
						</Text>
					</View>
				)}
			</View>

			<View style={postStyles.footer}>
				<TouchableOpacity style={postStyles.actionButton} onPress={handleLike}>
					<Ionicons name={isLiked ? "heart" : "heart-outline"} size={20} color={isLiked ? theme.colors.error : theme.colors.textSecondary} />
					{localLikeCount > 0 && <Text style={postStyles.actionCount}>{localLikeCount}</Text>}
				</TouchableOpacity>
				<TouchableOpacity style={postStyles.actionButton}>
					<Ionicons name="chatbubble-outline" size={18} color={theme.colors.textSecondary} />
					{content.commentCount > 0 && <Text style={postStyles.actionCount}>{content.commentCount}</Text>}
				</TouchableOpacity>
			</View>
		</View>
	);
};

const ScrollableCanvas = ({ activeTab, onComponentSelect }) => {
	const theme = useTheme();
	const styles = useStyles();
	const { fetchContent, content: contentFromContext } = useContent();
	const { tabs } = fetchContent("profileHeader") || { tabs: [] };
	const handlePress = () => {
		console.log("contentFromContext", contentFromContext);
		const tabName = tabs.find((t) => t.id === activeTab)?.label || "Unknown Tab";
		if (onComponentSelect && contentFromContext?.profile?.[activeTab]?.content) {
			onComponentSelect({
				componentType: `profile`,
				sections: [
					{
						id: "canvasType",
						title: "Canvas Type for " + tabName,
						properties: [`${activeTab}.content`],
					},
					{
						id: "gemitems",
						title: "Gems for " + tabName,
						properties: [`${activeTab}.gemitems`],
					},
				],
				properties: {
					[`${activeTab}.content`]: {
						store: "content",
						type: "dropdown",
						label: "Type",
						description: "Type of scrollable canvas",
						options: ["posts", "gems"],
					},
					[`${activeTab}.gemitems`]: {
						store: "content",
						type: "gemitems",
						label: "Gems",
						description: "Manage the list of gems",
					},
				},
			});
		}
	};
	// Determine what to render based on the activeTab and content type
	const renderCanvas = () => {
		// Default empty canvas view
		let tabView = (
			<View style={styles.emptyCanvas}>
				<Text style={styles.emptyCanvasText}>{tabs.find((t) => t.id === activeTab)?.label || "Unknown Tab"}</Text>
				<Text style={styles.emptyCanvasSubtext}>This tab is empty. Add components to build your page.</Text>
			</View>
		);
		// Check if we have content for this tab
		let tabContent = contentFromContext?.profile?.[activeTab];
		switch (tabContent?.content) {
			case "canvas":
				tabView = (
					<ScrollView style={{ flex: 1, width: "100%" }}>
						{[...Array(100)].map((_, i) => (
							<Text key={i}>Demo Item {i + 1}</Text>
						))}
					</ScrollView>
				);
				break;
			case "gems":
				// Helper function to render a gem component
				const renderGem = (gem) => {
					const { id, header, size = "half", dataType, dataConfig = {} } = gem;

					// Render gem content based on dataType
					let gemContent;

					switch (dataType) {
						case "progressbar":
							const mockValue = 12; // Mock value for preview
							const maxValue = dataConfig.maxValue || 52;
							const progress = (mockValue / maxValue) * 100;

							gemContent = (
								<View style={{ width: "100%" }}>
									<View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
										<Text style={{ color: "#666666", fontSize: 12 }}>
											{mockValue} of {maxValue} {dataConfig.unit || ""}
										</Text>
										{dataConfig.showPercentage && <Text style={{ color: "#007AFF", fontSize: 12, fontWeight: "500" }}>{Math.round(progress)}%</Text>}
									</View>
									<View style={{ height: 8, backgroundColor: "#E0E0E0", borderRadius: 4, overflow: "hidden", width: "100%" }}>
										<View style={{ height: "100%", backgroundColor: "#007AFF", borderRadius: 4, width: `${progress}%` }} />
									</View>
								</View>
							);
							break;

						case "value":
							gemContent = (
								<View style={{ width: "100%" }}>
									<Text style={{ color: "#1C1C1E", fontSize: 28, fontWeight: "bold" }}>
										{dataConfig.prefix || ""}
										{38}
										{dataConfig.suffix || ""}
									</Text>
									<Text style={{ color: "#8A8A8E", fontSize: 13, marginTop: 2 }}>{dataConfig.unit || ""}</Text>
								</View>
							);
							break;

						case "icon":
							gemContent = (
								<View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
									{dataConfig.iconName && <FontAwesome6 name={dataConfig.iconName} size={24} color="#1C1C1E" />}
									<Text style={{ color: "#1C1C1E", fontSize: 18, fontWeight: "500" }}>10 {dataConfig.text || ""}</Text>
								</View>
							);
							break;

						default:
							gemContent = <Text style={{ color: "#8A8A8E", fontSize: 13 }}>Unknown data type</Text>;
					}

					// Return the gem component with flex-based sizing
					return (
						<View
							key={id}
							style={{
								flex: size === "full" ? 1 : 0.5,
								maxWidth: size === "full" ? "100%" : "50%",
								backgroundColor: "#FFFFFF",
								borderRadius: 12,
								padding: 16,
								margin: 4,
								shadowColor: "#000",
								shadowOffset: { width: 0, height: 1 },
								shadowOpacity: 0.1,
								shadowRadius: 3,
								elevation: 2,
							}}>
							{header && (
								<Text
									style={{
										color: "#333333",
										fontSize: 16,
										fontWeight: "600",
										marginBottom: 12,
									}}>
									{header}
								</Text>
							)}
							{gemContent}
						</View>
					);
				};

				// Group and render gems with responsive layout
				tabView = (
					<ScrollView style={{ flex: 1, width: "100%" }} contentContainerStyle={{ padding: 8 }}>
						<View style={{ flexDirection: "row", flexWrap: "wrap" }}>
							{(() => {
								const gems = tabContent?.gemitems || [];
								const result = [];
								let currentRow = [];
								let currentRowSize = 0;

								// Process all gems for responsive layout
								gems.forEach((gem, index) => {
									const gemSize = gem.size === "full" ? 1 : 0.5;

									// If adding this gem would exceed a row's width
									if (currentRowSize + gemSize > 1) {
										// Push the current row to results
										result.push(
											<View
												key={`row-${index}`}
												style={{
													flexDirection: "row",
													width: "100%",
													justifyContent: currentRow.length === 1 ? "flex-start" : "space-between",
													marginBottom: 8,
												}}>
												{currentRow.map((g) => renderGem(g))}
											</View>
										);
										// Start a new row with this gem
										currentRow = [gem];
										currentRowSize = gemSize;
									} else {
										// Add to current row
										currentRow.push(gem);
										currentRowSize += gemSize;
									}
								});

								// Don't forget any items still in the current row
								if (currentRow.length > 0) {
									result.push(
										<View
											key="final-row"
											style={{
												flexDirection: "row",
												width: "100%",
												justifyContent: currentRow.length === 1 ? "flex-start" : "space-between",
												marginBottom: 8,
											}}>
											{currentRow.map((g) => renderGem(g))}
										</View>
									);
								}

								return result;
							})()}
						</View>
					</ScrollView>
				);
				break;
			case "posts":
				tabView = (
					<ScrollView style={{ flex: 1, width: "100%" }} contentContainerStyle={{ paddingBottom: 20 }}>
						{mockPosts.map((post) => (
							<MockPost key={post.id} content={post} />
						))}
					</ScrollView>
				);
				break;
			default:
				// Use the default empty canvas view
				break;
		}

		return tabView;
	};

	return <TouchableOpacity onPress={handlePress}>{renderCanvas()}</TouchableOpacity>;
};

const useStyles = () => {
	const theme = useTheme();

	return StyleSheet.create({
		emptyCanvas: {
			flex: 1,
			justifyContent: "center",
			alignItems: "center",
			minHeight: 300,
			backgroundColor: theme.colors.systemBackground.secondary,
			borderRadius: theme.borderRadius.lg,
			padding: theme.spacing.xl,
			marginVertical: theme.spacing.md,
			borderWidth: 2,
			borderStyle: "dashed",
			borderColor: theme.colors.separator,
		},
		emptyCanvasText: {
			...theme.typography.title3,
			color: theme.colors.textSecondary,
			marginBottom: theme.spacing.sm,
		},
		emptyCanvasSubtext: {
			...theme.typography.caption1,
			color: theme.colors.textTertiary,
		},
	});
};

const usePostStyles = () => {
	const theme = useTheme();
	return StyleSheet.create({
		container: {
			backgroundColor: theme.colors.systemBackground.primary,
			borderRadius: theme.borderRadius.md,
			marginBottom: theme.spacing.md,
			padding: theme.spacing.sm,
			borderWidth: 1,
			borderColor: theme.colors.separator,
		},
		header: {
			flexDirection: "row",
			justifyContent: "space-between",
			alignItems: "center",
			marginBottom: theme.spacing.xs,
		},
		headerLeft: {
			flexDirection: "row",
			alignItems: "center",
		},
		avatar: {
			width: 35,
			height: 35,
			borderRadius: 17.5,
			marginRight: theme.spacing.sm,
		},
		authorContainer: {
			justifyContent: "center",
		},
		authorName: {
			...theme.typography.subhead,
			fontWeight: "600",
			color: theme.colors.text,
		},
		timestamp: {
			...theme.typography.caption,
			color: theme.colors.textSecondary,
		},
		featuredHeader: {
			flexDirection: "row",
			alignItems: "center",
			marginBottom: theme.spacing.sm,
			paddingBottom: theme.spacing.xs,
			borderBottomWidth: 1,
			borderBottomColor: theme.colors.separator,
		},
		featuredLabel: {
			...theme.typography.headline,
			fontWeight: "600",
			color: theme.colors.primary,
			flexShrink: 1,
		},
		contentContainer: {
			marginBottom: theme.spacing.xs,
		},
		postText: {
			...theme.typography.body,
			color: theme.colors.text,
			lineHeight: 18,
			marginBottom: theme.spacing.xs,
		},
		imageContainer: {
			marginTop: theme.spacing.xs,
			marginBottom: theme.spacing.sm,
			width: "100%",
			aspectRatio: 16 / 9,
			borderRadius: theme.borderRadius.md,
			overflow: "hidden",
			position: "relative",
			backgroundColor: theme.colors.systemBackground.secondary,
		},
		postImagePreview: {
			width: "100%",
			height: "100%",
		},
		moreImagesOverlay: {
			...StyleSheet.absoluteFillObject,
			backgroundColor: "rgba(0,0,0,0.4)",
			justifyContent: "center",
			alignItems: "center",
			borderRadius: theme.borderRadius.md,
		},
		moreImagesText: {
			...theme.typography.caption,
			color: "white",
			...theme.typography.headline,
			fontWeight: "bold",
		},
		eventPreview: {
			marginTop: theme.spacing.xs,
			padding: theme.spacing.xs,
			backgroundColor: theme.colors.systemBackground.secondary,
			borderRadius: theme.borderRadius.sm,
		},
		eventTitle: {
			...theme.typography.caption,
			fontWeight: "600",
			color: theme.colors.text,
		},
		eventDetails: {
			...theme.typography.caption2,
			color: theme.colors.textSecondary,
		},
		footer: {
			flexDirection: "row",
			marginTop: theme.spacing.xs,
			paddingTop: theme.spacing.xs,
			borderTopWidth: 1,
			borderTopColor: theme.colors.separator,
		},
		actionButton: {
			flexDirection: "row",
			alignItems: "center",
			marginRight: theme.spacing.md,
		},
		actionCount: {
			...theme.typography.caption,
			marginLeft: theme.spacing.xxs,
			color: theme.colors.textSecondary,
		},
	});
};

const ScrollableCanvasWrapper = (props) => {
	const theme = useTheme();
	const pageContext = useProfilePage();
	const WrappedScrollableCanvas = withComponentEditor(ScrollableCanvas);

	return <WrappedScrollableCanvas {...props} theme={theme} pageContext={pageContext} />;
};

export default ScrollableCanvasWrapper;
